import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import * as api from "../api/storageApi";
import type { PublicLink } from "../api/storageApi";
import ErrorModal from "../components/ErrorModal";

type Filter = "all" | "active" | "inactive" | "expired";

const formatBytes = (bytes: number): string => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const isExpired = (link: PublicLink): boolean => {
  if (!link.expires_at) return false;
  return new Date(link.expires_at).getTime() < Date.now();
};

export default function PublicLinksPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuthenticator((ctx) => [ctx.user]);
  const userLabel = user?.signInDetails?.loginId ?? user?.username ?? "";
  const userInitial = (userLabel.trim()[0] ?? "?").toUpperCase();

  const [links, setLinks] = useState<PublicLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [deactivating, setDeactivating] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState<{ title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listPublicLinks();
      setLinks(data);
    } catch (err) {
      console.error("Error loading public links:", err);
      setErrorModal({
        title: "Error al cargar enlaces",
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return links.filter((l) => {
      if (q && !l.original_name.toLowerCase().includes(q) && !l.id.toLowerCase().includes(q)) return false;
      const expired = isExpired(l);
      switch (filter) {
        case "active":
          return l.is_active && !expired;
        case "inactive":
          return !l.is_active;
        case "expired":
          return expired;
        default:
          return true;
      }
    });
  }, [links, search, filter]);

  // const stats = useMemo(() => {
  //   const active = links.filter((l) => l.is_active && !isExpired(l)).length;
  //   const inactive = links.filter((l) => !l.is_active).length;
  //   const expired = links.filter((l) => isExpired(l)).length;
  //   return { total: links.length, active, inactive, expired };
  // }, [links]);

  const handleDeactivate = async (id: string) => {
    setDeactivating(id);
    try {
      await api.deactivatePublicLink(id);
      setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, is_active: false } : l)));
      setConfirmId(null);
    } catch (err) {
      console.error("Error deactivating link:", err);
      setErrorModal({
        title: "Error al desactivar",
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setDeactivating(null);
    }
  };

  const handleCopy = async (link: PublicLink) => {
    try {
      await navigator.clipboard.writeText(link.publicUrl);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = link.publicUrl;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopiedId(link.id);
        setTimeout(() => setCopiedId(null), 2000);
      } catch {}
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="d-flex flex-column vh-100" style={{ background: "#f8fafc", color: "#1e293b" }}>
      {/* Navbar */}
      <nav className="navbar navbar-expand feebbo-navbar px-3 py-2" style={{ zIndex: 100 }}>
        <button className="btn btn-sm btn-feebbo-outline me-2" onClick={() => navigate("/")}>
          <i className="bi bi-arrow-left me-1" />
          Volver
        </button>
        <span className="navbar-brand d-flex align-items-center gap-2 mb-0">
          <span style={{ fontSize: 18, color: "#2563eb", fontWeight: 700 }}>■</span>
          <span style={{ color: "#1e293b", fontWeight: 600, fontSize: 16 }}>
            Feebbo<span style={{ color: "#2563eb" }}>Drive</span>
          </span>
          <span className="ms-2 text-muted" style={{ fontSize: 14 }}>· Enlaces públicos</span>
        </span>

        <div className="ms-auto d-flex align-items-center gap-2">
          <div className="feebbo-userchip d-flex align-items-center gap-2 px-2 py-1">
            <div className="feebbo-avatar" aria-hidden="true">
              {userInitial}
            </div>
            <span className="small text-truncate d-none d-md-inline" style={{ maxWidth: 220 }}>
              {userLabel}
            </span>
          </div>
          <button className="btn btn-sm btn-feebbo-outline" onClick={() => signOut()}>
            <i className="bi bi-box-arrow-right me-1" />
            Salir
          </button>
        </div>
      </nav>

      {/* Toolbar */}
      <div className="px-4 py-3 d-flex align-items-center gap-3 flex-wrap" style={{ background: "#ffffff", borderBottom: "1px solid #e2e8f0" }}>
        {/* <div className="d-flex gap-3">
          <StatCard label="Total" value={stats.total} color="#2563eb" />
          <StatCard label="Activos" value={stats.active} color="#16a34a" />
          <StatCard label="Inactivos" value={stats.inactive} color="#64748b" />
          <StatCard label="Expirados" value={stats.expired} color="#dc2626" />
        </div> */}

        <div className="ms-auto d-flex align-items-center gap-2">
          <div className="input-group input-group-sm" style={{ width: 280 }}>
            <span className="input-group-text feebbo-input" style={{ borderRight: "none" }}>
              <i className="bi bi-search" style={{ color: "#94a3b8" }} />
            </span>
            <input
              type="text"
              className="form-control feebbo-input"
              style={{ borderLeft: "none" }}
              placeholder="Buscar por archivo o ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="btn btn-feebbo-outline btn-sm" onClick={() => setSearch("")}>
                <i className="bi bi-x" />
              </button>
            )}
          </div>

          <select
            className="form-select form-select-sm"
            style={{ width: "auto", minWidth: 130 }}
            value={filter}
            onChange={(e) => setFilter(e.target.value as Filter)}
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
            <option value="expired">Expirados</option>
          </select>

          <button className="btn btn-sm btn-feebbo-outline" onClick={load} disabled={loading}>
            <i className={`bi bi-arrow-clockwise ${loading ? "spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-grow-1 overflow-auto px-4 py-3">
        {loading && links.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center">
              <div className="spinner-border mb-3" style={{ color: "#2563eb" }} />
              <p style={{ color: "#64748b" }}>Cargando enlaces…</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center">
              <i className="bi bi-link-45deg d-block mb-2" style={{ fontSize: 48, color: "#94a3b8" }} />
              <p style={{ color: "#64748b" }}>
                {links.length === 0 ? "No hay enlaces públicos creados" : "Ningún enlace coincide con los filtros"}
              </p>
            </div>
          </div>
        ) : (
          <div className="table-responsive bg-white rounded" style={{ border: "1px solid #e2e8f0" }}>
            <table className="table feebbo-table mb-0 align-middle">
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>Estado</th>
                  <th className="text-end">Tamaño</th>
                  <th className="text-end">Accesos</th>
                  <th>Último acceso</th>
                  <th>Creado</th>
                  <th>Expira</th>
                  <th style={{ width: 220 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((link) => {
                  const expired = isExpired(link);
                  const status = !link.is_active
                    ? { label: "Inactivo", color: "#64748b", bg: "#f1f5f9" }
                    : expired
                    ? { label: "Expirado", color: "#dc2626", bg: "#fef2f2" }
                    : { label: "Activo", color: "#16a34a", bg: "#f0fdf4" };
                  const isConfirming = confirmId === link.id;

                  return (
                    <tr key={link.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                          <i className="bi bi-file-earmark" style={{ color: "#64748b" }} />
                          <div style={{ minWidth: 0 }}>
                            <div className="text-truncate" style={{ maxWidth: 320 }} title={link.original_name}>
                              {link.original_name}
                            </div>
                            <small className="text-muted text-truncate d-block" style={{ maxWidth: 320 }} title={link.id}>
                              {link.id}
                            </small>
                            {link.created_by_email && (
                              <small className="text-muted text-truncate d-block" style={{ maxWidth: 320, fontSize: 11 }} title={link.created_by_email}>
                                <i className="bi bi-person me-1" />{link.created_by_email}
                              </small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className="badge rounded-pill"
                          style={{
                            color: status.color,
                            background: status.bg,
                            border: `1px solid ${status.color}33`,
                            fontWeight: 500,
                          }}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="text-end">{formatBytes(link.file_size)}</td>
                      <td className="text-end">{link.access_count}</td>
                      <td><small>{formatDate(link.last_accessed_at)}</small></td>
                      <td><small>{formatDate(link.created_at)}</small></td>
                      <td><small>{formatDate(link.expires_at)}</small></td>
                      <td>
                        {isConfirming ? (
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeactivate(link.id)}
                              disabled={deactivating === link.id}
                            >
                              {deactivating === link.id ? (
                                <><span className="spinner-border spinner-border-sm me-1" />Desactivando…</>
                              ) : (
                                <>Confirmar</>
                              )}
                            </button>
                            <button
                              className="btn btn-sm btn-feebbo-outline"
                              onClick={() => setConfirmId(null)}
                              disabled={deactivating === link.id}
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-feebbo-outline"
                              onClick={() => handleCopy(link)}
                              title="Copiar URL"
                            >
                              <i className={`bi ${copiedId === link.id ? "bi-check2" : "bi-clipboard"}`} />
                            </button>
                            <a
                              className="btn btn-sm btn-feebbo-outline"
                              href={link.publicUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Abrir"
                            >
                              <i className="bi bi-box-arrow-up-right" />
                            </a>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setConfirmId(link.id)}
                              disabled={!link.is_active}
                              title={link.is_active ? "Desactivar enlace" : "Ya inactivo"}
                            >
                              <i className="bi bi-slash-circle me-1" />
                              Desactivar
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {errorModal && (
        <ErrorModal
          title={errorModal.title}
          message={errorModal.message}
          onClose={() => setErrorModal(null)}
        />
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg) } }
      `}</style>
    </div>
  );
}

// function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
//   return (
//     <div
//       className="d-flex flex-column px-3 py-1 rounded"
//       style={{ background: "#f8fafc", border: "1px solid #e2e8f0", minWidth: 90 }}
//     >
//       <small className="text-muted" style={{ fontSize: 11 }}>{label}</small>
//       <strong style={{ color, fontSize: 18 }}>{value}</strong>
//     </div>
//   );
// }
